<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import User from '@/api/User'
import moment from 'moment'
import { extractTimeFromQuery, formatTimeRange } from '@/components/EventSearch/timeRangeUtils'
import { useDataSourcesStore } from '@/stores/dataSources'

const router = useRouter()
const dataSourcesStore = useDataSourcesStore()

const props = defineProps({
  fieldMappings: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['execute-query', 'explore-log-type', 'explore-source'])

// Color palette (matches LogTypesSidebar / InteractiveHistogram)
const LOG_TYPE_COLORS = {
  conn: '#7357D2',
  dns: '#4A90D9',
  http: '#50C878',
  ssl: '#F5A623',
  x509: '#D94A4A',
  files: '#9B59B6',
  smtp: '#1ABC9C',
  ssh: '#E74C3C',
  dhcp: '#3498DB',
  ntp: '#95A5A6',
  rdp: '#E67E22',
  ftp: '#27AE60',
  sip: '#8E44AD',
  snmp: '#2980B9',
  kerberos: '#C0392B',
  smb: '#16A085',
  dce_rpc: '#D35400',
  ntlm: '#7F8C8D',
  pe: '#BDC3C7',
  weird: '#E91E63',
  notice: '#FF5722',
  software: '#607D8B',
  known: '#795548',
  _default: '#6B7280'
}

const exampleQueries = [
  {
    title: 'Successful SSH Sessions',
    description: 'Find all authenticated SSH connections across your sources',
    query: 'auth_success:"true"',
    icon: ['fas', 'key']
  },
  {
    title: 'DNS to Abused TLDs',
    description: 'DNS queries to top-level domains frequently associated with malicious activity',
    query: 'query:*.xyz OR query:*.top OR query:*.buzz OR query:*.tk OR query:*.ml',
    icon: ['fas', 'globe']
  },
  {
    title: 'Scripting User Agents',
    description: 'HTTP requests from curl, wget, python, and other non-browser clients',
    query: 'user_agent:*curl* OR user_agent:*wget* OR user_agent:*python* OR user_agent:*Go-http*',
    icon: ['fas', 'code']
  },
  {
    title: 'Large HTTP POSTs',
    description: 'HTTP POST requests with unusually large request bodies',
    query: 'method:POST AND request_body_len:>100000',
    icon: ['fas', 'arrow-up-right-from-square']
  },
  {
    title: 'SSH Brute Force Attempts',
    description: 'Failed SSH sessions with multiple authentication attempts',
    query: 'auth_success:"false" AND auth_attempts:>3',
    icon: ['fas', 'lock']
  },
  {
    title: 'Long-Lived Connections',
    description: 'Connections lasting over an hour — may indicate tunneling or persistent access',
    query: 'duration:>3600',
    icon: ['fas', 'clock']
  }
]

const syntaxRef = [
  { label: 'Field match', example: 'ip:1.2.3.4' },
  { label: 'Domain search', example: 'domain:example.com' },
  { label: 'Wildcard', example: 'query:*.evil.com' },
  { label: 'Port range', example: 'dst_port:>1024' },
  { label: 'Boolean', example: 'A AND B OR NOT C' },
  { label: 'Grouping', example: '(A OR B) AND C' },
  { label: 'Hash lookup', example: 'hash:abc123...' },
  { label: 'Meta fields', example: 'ip, domain, hash, email, sni, ua, mime' }
]

// Recent queries
const recentQueries = ref([])
const recentLoading = ref(false)

const availableLogTypes = computed(() => {
  return Object.keys(props.fieldMappings).sort()
})

function getColor(logType) {
  return LOG_TYPE_COLORS[logType] || LOG_TYPE_COLORS._default
}

function handleQueryClick(query) {
  emit('execute-query', query, '7d')
}

function handleLogTypeClick(logType) {
  emit('explore-log-type', logType)
}

const sourceGroups = computed(() => {
  return dataSourcesStore.activeSourceGroups.map(group => ({
    ...group,
    items: dataSourcesStore.activeSources.filter(s => s.group === group.id)
  }))
})

function handleSourceClick(sourceId) {
  emit('explore-source', sourceId)
}

function handleRecentQueryClick(queryText) {
  emit('execute-query', queryText)
}

function goToHistory() {
  router.push({ path: '/search/event/history' })
}

function formatTime(timestamp) {
  if (!timestamp) return ''
  return moment(timestamp).fromNow()
}

function parseQuery(queryString) {
  const { cleanQuery, start, end } = extractTimeFromQuery(queryString)
  return {
    text: cleanQuery || queryString,
    timeLabel: formatTimeRange(start, end)
  }
}

async function fetchRecentQueries() {
  recentLoading.value = true
  try {
    const response = await User.getQueries(0, 5, '', '', '', 'event')
    if (response.status === 'success') {
      recentQueries.value = response.data?.queries || []
    }
  } catch (err) {
    console.error('Failed to fetch recent queries:', err)
  } finally {
    recentLoading.value = false
  }
}

onMounted(() => {
  fetchRecentQueries()
})
</script>

<template>
  <div class="landing-view">
    <div class="landing-scroll">
      <!-- Hero -->
      <div class="hero">
        <h1 class="hero-title">Explore Network Events</h1>
        <p class="hero-subtitle">Search directly across all raw data from your connected sources to uncover threats and anomalies</p>
      </div>

      <!-- Example Queries -->
      <div class="section">
        <h2 class="section-title">
          <font-awesome-icon :icon="['fas', 'crosshairs']" class="section-icon" />
          Start Hunting
        </h2>
        <div class="query-grid">
          <div
            v-for="eq in exampleQueries"
            :key="eq.title"
            class="query-card"
            @click="handleQueryClick(eq.query)"
          >
            <div class="query-card-header">
              <font-awesome-icon :icon="eq.icon" class="query-card-icon" />
              <span class="query-card-title">{{ eq.title }}</span>
            </div>
            <p class="query-card-desc">{{ eq.description }}</p>
            <code class="query-card-code">{{ eq.query }}</code>
          </div>
        </div>
      </div>

      <!-- Sources & Log Types -->
      <div class="section">
        <div class="sources-logtypes-grid">
          <!-- Left: Sources -->
          <div class="sources-column">
            <h2 class="section-title">
              <font-awesome-icon :icon="['fas', 'database']" class="section-icon" />
              Sources
            </h2>
            <div class="source-groups">
              <div
                v-for="group in sourceGroups"
                :key="group.id"
                class="source-group-card"
                @click="handleSourceClick(group.id)"
              >
                <div class="source-group-row">
                  <span class="source-group-accent" :style="{ backgroundColor: group.accent }"></span>
                  <span class="source-group-label">{{ group.label }}</span>

                  <span class="source-group-items">
                    <span v-for="(item, i) in group.items" :key="item.id" class="source-item">
                      <font-awesome-icon :icon="['fas', item.icon]" class="source-item-icon" />
                      <span class="source-item-name">{{ item.label }}</span>
                      <span v-if="i < group.items.length - 1" class="source-item-sep">&middot;</span>
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Right: Log Types -->
          <div class="logtypes-column">
            <h2 class="section-title">
              <font-awesome-icon :icon="['fas', 'layer-group']" class="section-icon" />
              Log Types
            </h2>
            <div class="log-type-list">
              <button
                v-for="lt in availableLogTypes"
                :key="lt"
                class="log-type-item"
                @click="handleLogTypeClick(lt)"
              >
                <span class="log-type-color" :style="{ backgroundColor: getColor(lt) }"></span>
                <span class="log-type-name">{{ lt }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Query Syntax Reference -->
      <div class="section">
        <h2 class="section-title">
          <font-awesome-icon :icon="['fas', 'code']" class="section-icon" />
          Query Syntax
        </h2>
        <div class="syntax-grid">
          <div v-for="s in syntaxRef" :key="s.label" class="syntax-item">
            <span class="syntax-label">{{ s.label }}</span>
            <code class="syntax-example">{{ s.example }}</code>
          </div>
        </div>
      </div>

      <!-- Recent Queries -->
      <div v-if="recentQueries.length > 0" class="section">
        <div class="section-title-row">
          <h2 class="section-title">
            <font-awesome-icon :icon="['fas', 'clock-rotate-left']" class="section-icon" />
            Recent Queries
          </h2>
          <button class="view-all-btn" @click="goToHistory">View all</button>
        </div>
        <div class="recent-list">
          <div
            v-for="(q, i) in recentQueries"
            :key="q.id || i"
            class="recent-item"
            @click="handleRecentQueryClick(q.query)"
          >
            <div class="recent-query-content">
              <code class="recent-query">{{ parseQuery(q.query).text }}</code>
              <span class="time-badge">{{ parseQuery(q.query).timeLabel }}</span>
            </div>
            <span class="recent-time">{{ formatTime(q.timestamp) }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.landing-view {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #151a22;
  border-radius: 0.75rem;
  overflow: hidden;
}

.landing-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 2rem 2.5rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

/* Hero */
.hero {
  text-align: center;
  padding: 1.5rem 0 0.5rem;
}

.hero-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #f3f4f6;
  margin: 0 0 0.5rem;
}

.hero-subtitle {
  color: #9ca3af;
  font-size: 0.9rem;
  margin: 0;
  max-width: 520px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.5;
}

/* Sections */
.section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.section-title {
  font-size: 0.8rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.section-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.section-icon {
  font-size: 0.75rem;
  opacity: 0.7;
}

/* Query Cards */
.query-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
}

@media (max-width: 1200px) {
  .query-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 800px) {
  .query-grid {
    grid-template-columns: 1fr;
  }
}

.query-card {
  background: rgba(26, 29, 36, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 0.625rem;
  padding: 1rem 1.125rem;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}


.query-card:hover {
  background: rgba(115, 87, 210, 0.08);
  border-color: rgba(115, 87, 210, 0.3);
}

.query-card-header {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}

.query-card-icon {
  color: #7357D2;
  font-size: 0.875rem;
  opacity: 0.8;
  flex-shrink: 0;
}

.query-card-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #e5e7eb;
}

.query-card-desc {
  color: #9ca3af;
  font-size: 0.775rem;
  margin: 0;
  line-height: 1.4;
}

.query-card-code {
  color: #c4b5fd;
  font-size: 0.7rem;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  background: rgba(115, 87, 210, 0.08);
  padding: 0.375rem 0.5rem;
  border-radius: 0.375rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: auto;
}

/* Log Type List (inline wrapping items) */
.log-type-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}

.log-type-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.log-type-item:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.15);
}

.log-type-color {
  width: 8px;
  height: 8px;
  border-radius: 2px;
  flex-shrink: 0;
}

.log-type-name {
  font-size: 0.85rem;
  font-weight: 600;
  color: #E5E7EB;
  text-transform: uppercase;
}

/* Syntax Reference */
.syntax-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.5rem;
}

@media (max-width: 1200px) {
  .syntax-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.syntax-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.625rem 0.75rem;
  background: rgba(26, 29, 36, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 0.5rem;
}

.syntax-label {
  font-size: 0.7rem;
  color: #6b7280;
  font-weight: 500;
}

.syntax-example {
  font-size: 0.75rem;
  color: #c4b5fd;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
}

/* Recent Queries */
.view-all-btn {
  background: transparent;
  border: none;
  color: #7357D2;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  transition: all 0.15s ease;
}

.view-all-btn:hover {
  color: #9B7EE8;
  background: rgba(115, 87, 210, 0.1);
}

.recent-list {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.recent-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.625rem 0.75rem;
  background: rgba(26, 29, 36, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.15s ease;
}

.recent-item:hover {
  background: rgba(115, 87, 210, 0.08);
  border-color: rgba(115, 87, 210, 0.25);
}

.recent-query-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  overflow: hidden;
}

.recent-query {
  color: #c4b5fd;
  font-size: 0.8rem;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.time-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.125rem 0.5rem;
  background: rgba(115, 87, 210, 0.15);
  border-radius: 0.375rem;
  font-size: 0.675rem;
  color: #a78bfa;
  white-space: nowrap;
  flex-shrink: 0;
}

.recent-time {
  font-size: 0.7rem;
  color: #6b7280;
  white-space: nowrap;
  flex-shrink: 0;
}

/* Sources & Log Types Grid */
.sources-logtypes-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
}

@media (max-width: 900px) {
  .sources-logtypes-grid {
    grid-template-columns: 1fr;
  }
}

.sources-column,
.logtypes-column {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.source-groups {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.source-group-card {
  background: rgba(26, 29, 36, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 0.625rem;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
  overflow: hidden;
}

.source-group-card:hover {
  background: rgba(115, 87, 210, 0.08);
  border-color: rgba(115, 87, 210, 0.3);
}

.source-group-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  overflow: hidden;
  white-space: nowrap;
}

.source-group-accent {
  width: 10px;
  height: 10px;
  border-radius: 3px;
  flex-shrink: 0;
}

.source-group-label {
  font-size: 0.925rem;
  font-weight: 600;
  color: #e5e7eb;
  flex-shrink: 0;
}

.source-group-items {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.source-item {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  flex-shrink: 0;
}

.source-item-icon {
  color: #6b7280;
  font-size: 0.7rem;
  flex-shrink: 0;
}

.source-item-name {
  font-size: 0.775rem;
  font-weight: 500;
  color: #9ca3af;
}

.source-item-sep {
  color: #4b5563;
  font-size: 0.775rem;
  flex-shrink: 0;
}

/* Scrollbar */
.landing-scroll::-webkit-scrollbar {
  width: 6px;
}

.landing-scroll::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.landing-scroll::-webkit-scrollbar-thumb {
  background: rgba(115, 87, 210, 0.25);
  border-radius: 3px;
}

.landing-scroll::-webkit-scrollbar-thumb:hover {
  background: rgba(115, 87, 210, 0.4);
}
</style>
